import React, { useMemo } from 'react';
import styled from 'styled-components';
import type { IndexOrderTextData } from '../../shared/dto/EntityRepository';
import { shrinkString } from '../../shared/util/shrinkString';
import { useTextIndexing } from '../../shared/hooks/useTextIndexing';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteIcon from '../../asset/icon/delete.png';

/** IndexTextViewer 등에서 drop 시 getData 로 읽도록 동일 값 사용 */
export const TEXT_INDEX_DRAG_MIME_TYPE = 'text/plain';

interface IndexTextInfoProps {
  text: IndexOrderTextData;
  isDragging: boolean;
  /** 패널 안에서 블록 드래그에 묶을 행 선택 */
  isReorderPicked?: boolean;
  onToggleReorderPick?: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

const INDEX_ORDER_DISPLAY_OFFSET = 1;
const HANDLE_ICON = '::';

const IndexTextInfo: React.FC<IndexTextInfoProps> = ({
  text,
  isDragging,
  isReorderPicked = false,
  onToggleReorderPick,
  onDragStart,
  onDragEnd,
}) => {
  const { removeSelectedIndexingText } = useTextIndexing();

  const shrinkedTitle = useMemo(() => shrinkString(text.title, 48), [text.title]);
  const orderLabel = String(text.index + INDEX_ORDER_DISPLAY_OFFSET);

  const handleRemove = () => {
    removeSelectedIndexingText(text.id);
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData(TEXT_INDEX_DRAG_MIME_TYPE, String(text.id));
    e.dataTransfer.effectAllowed = 'move';
    onDragStart();
  };

  const handleDragEnd = () => {
    onDragEnd();
  };

  return (
    <Root
      $isDragging={isDragging}
      $isReorderPicked={isReorderPicked}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        onToggleReorderPick?.();
      }}
    >
      <DragBody
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Column $flex="0.35" $mutedLabel $justifyContent="flex-start">
          <DragHandle>{HANDLE_ICON}</DragHandle>
          {orderLabel}
        </Column>
        <Column $flex="2.5" $justifyContent="flex-start" $paddingLeft="10px">
          {shrinkedTitle}
        </Column>
      </DragBody>
      <Column $flex="0.15" $justifyContent="center">
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent={true}
          backgroundImg={deleteIcon}
          onClick={handleRemove}
        />
      </Column>
    </Root>
  );
};

const Root = styled.article<{ $isDragging: boolean; $isReorderPicked: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  ${({ theme }) => theme.typography.Body_03_2};
  color: ${({ theme }) => theme.colors.color_Gray_03};

  height: ${({ $isDragging }) => ($isDragging ? '10px' : '40px')};
  min-height: ${({ $isDragging }) => ($isDragging ? '10px' : '40px')};
  transform: ${({ $isDragging }) =>
    $isDragging ? 'scaleY(0.2) scaleX(0.5)' : 'scaleY(1) scaleX(1)'};
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
  transition: height 0.4s ease-out, transform 0.4s ease-out, opacity 0.4s ease-out;
  padding: 0 4px 0 8px;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  background-color: ${({ $isReorderPicked, theme }) =>
    $isReorderPicked ? theme.colors.color_Gray_06 : 'transparent'};
`;

const DragBody = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 0;
  cursor: grab;

  &:hover {
    transform: scale(0.98);
  }

  &:active {
    cursor: grabbing;
  }
`;

const DragHandle = styled.div`
  margin-right: 10px;
  ${({ theme }) => theme.typography.Body_03_1};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const Column = styled.div<{
  $flex: string;
  $justifyContent?: 'center' | 'flex-start';
  $paddingLeft?: string;
  $mutedLabel?: boolean;
}>`
  flex: ${({ $flex }) => $flex};
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: ${({ $justifyContent }) => $justifyContent ?? 'center'};
  padding-left: ${({ $paddingLeft }) => $paddingLeft ?? 0};

  user-select: none;
  color: ${({ theme, $mutedLabel }) =>
    $mutedLabel ? theme.colors.color_Gray_05 : 'inherit'};
`;

export default IndexTextInfo;
